import { Chip, IconButton, Stack, useTheme } from "@mui/material";
import { useAgentQuery } from "web_myplann/Api/Advisor/useAgentQuery";
import { useAssignedAgentsQuery } from "web_myplann/Api/Advisor/useAssignedAgentsQuery";
import { useTaskQuery } from "web_myplann/Api/Tasks/useTaskQuery";
import { useTaskWorkflowsQuery } from "web_myplann/Api/Tasks/useTaskWorkflowsQuery";
import { UserAgentStatusEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { DataCard } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { Steps } from "web_myplann/Shared/Components/Steps";
import { ToolBarMenu } from "web_myplann/Shared/Components/ToolBarMenu";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { dateFormatter } from "web_myplann/Shared/Utils/formatters";
import { useTaskMenuItems } from "web_myplann/Tasks/Hooks/useTaskMenuItems";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";
import { device } from "web_myplann/Utils/device";
import "dayjs/locale/cs";
import { ChartNoAxesGantt, Ellipsis, Info, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

export const TaskDetailPage = () => {
	const { id } = useParams<{ id: string }>();
	const theme = useTheme();
	const { t } = useResource();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const { data: taskData, isFetching: isTaskFetching } = useTaskQuery(
		Number(id),
	);
	const { data: taskWorkflowsData, isFetching: isTaskWorkflowsFetching } =
		useTaskWorkflowsQuery(Number(id));

	const { data: dataAgent } = useAgentQuery(taskData?.author.id ?? 0);
	const { data: assignedAgents } = useAssignedAgentsQuery(
		UserAgentStatusEnum.Connected,
	);

	const menuItems = useTaskMenuItems(dataAgent);

	const dateFormat = taskData?.dueDate?.endsWith("00:00:00")
		? "dd.MM.yyyy"
		: "dd.MM.yyyy HH:mm";

	return (
		<>
			<MyPlannLayout
				topBar={
					<NavigationHeader
						title={t(Resources.Task.Detail.title)}
						defaultRoute={DEFAULT_ROUTE}
						endComponent={
							<Stack direction="row" gap={1.6}>
								<IconButton
									sx={{ padding: 0 }}
									onClick={() => {
										device.navigateChatRequest(
											String(dataAgent?.conversationId ?? ""),
											dataAgent?.conversationTitle ?? "",
											dataAgent?.uid,
										);
									}}
								>
									<MessageCircle
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
								<IconButton
									sx={{ padding: 0 }}
									onClick={(event) => {
										event.stopPropagation();
										setAnchorEl(event.currentTarget);
									}}
									disableFocusRipple
									disableTouchRipple
								>
									<Ellipsis
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
							</Stack>
						}
					/>
				}
			>
				{isTaskFetching || isTaskWorkflowsFetching ? (
					<DetailSectionLoader />
				) : (
					<Stack paddingTop={1.6}>
						<DataCard
							icon={
								<Info size={16} color={theme.palette.deprecated.grey[700]} />
							}
							label={t(Resources.Task.Detail.BasicInfo.title)}
							noDivider={taskWorkflowsData?.items?.length === 0}
						>
							<DataRow
								label={t(Resources.Task.Detail.BasicInfo.Name)}
								value={taskData?.name}
							/>
							<DataRow
								label={t(Resources.Task.Detail.BasicInfo.Date)}
								value={dateFormatter(taskData?.dueDate, undefined, dateFormat)}
							/>
							{taskData?.statusName && (
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.State)}
									element={
										<Chip
											label={taskData?.statusName}
											variant="filled"
											sx={{
												fontWeight: 500,
												color: theme.palette.getContrastText(
													taskData?.statusColor ?? "#000",
												),
												backgroundColor: taskData?.statusColor,
												borderRadius: "9999px",
											}}
										/>
									}
								/>
							)}
							{assignedAgents?.data.length &&
								assignedAgents?.data.length > 1 && (
									<DataRow
										label={t(Resources.Task.Detail.BasicInfo.CreatedBy)}
										value={
											taskData
												? `${taskData.author.name} (${t(Resources.Dashboard.UserType[taskData.author.type])})`
												: ""
										}
									/>
								)}
							<DataRow
								label={t(Resources.Task.Detail.BasicInfo.Solver)}
								value={
									taskData?.assignedTo
										? `${taskData.assignedTo.name} (${t(Resources.Dashboard.UserType[taskData.assignedTo.type])})`
										: ""
								}
							/>
							<DataNote
								label={t(Resources.Task.Detail.BasicInfo.Desc)}
								text={taskData?.description}
							/>
						</DataCard>

						<DataCard
							icon={
								<ChartNoAxesGantt
									size={16}
									color={theme.palette.deprecated.grey[700]}
								/>
							}
							label={t(Resources.Task.Detail.Process.title)}
							noDivider
						>
							<Stack gap={0.6}>
								{taskWorkflowsData?.items?.map((w) => (
									<Steps key={w.taskWorkflowID} name={w.name} steps={w.steps} />
								))}
							</Stack>
						</DataCard>
					</Stack>
				)}
			</MyPlannLayout>
			<ToolBarMenu
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
				items={menuItems}
			/>
		</>
	);
};
